import { out } from "../output/out";
import { formatAdvisorPart } from "@/lib/event/format-advisor-part";
import { formatDeliverablePart } from "@/lib/event/format-deliverable-part";
import { formatToolPart } from "@/lib/event/format-tool-part";
import type { TranscriptEvent } from "@/lib/event/transcript-event";
import { truncate } from "@/lib/truncate/truncate";

/**
 * One transcript event as one readable block.
 *
 * The transcript is the only honest account of what the agent did, so the
 * shapes that matter to a reviewer — tool calls, file changes, questions,
 * guardrails, errors — each keep a distinct marker instead of collapsing into
 * prose.
 */
export function formatEvent(
  event: TranscriptEvent,
  baseUrl: string,
): string | null {
  const part = event.part;

  switch (part.kind) {
    case "text":
      if (part.authorUserId) {
        return `${out.paint("you", "magenta")}  ${truncate(part.text)}`;
      }
      // Text with no author but a source was injected by the platform (an
      // integration trigger, an inbox event): the transcript contract says it
      // must never be attributed to the model.
      if (part.source) {
        return out.paint(`system ${truncate(part.text, 400)}`, "dim");
      }
      return `${out.paint("agent", "blue")}  ${truncate(part.text)}`;
    case "reasoning":
      return out.paint(`think  ${truncate(part.text, 400)}`, "dim");
    case "tool":
      return formatToolPart(part);
    case "file_change":
      return `${out.paint("file", "dim")}   ${part.changeType.padEnd(8)} ${out.cell(part.path)}`;
    case "question":
      return out.paint(
        `ask    question round ${part.questionBatchId} — answer with \`hepha questions answer ${part.questionBatchId}\``,
        "yellow",
      );
    // No CLI verb decides a plan: the accept/refuse pair lives in the app.
    case "plan":
      return out.paint(
        `plan   plan ${part.planProposalId} awaits accept/refuse in the app`,
        "yellow",
      );
    case "approval":
      return out.paint(
        `gate   guardrail ${part.guardrailRequestId} needs a human — \`hepha approvals show ${part.guardrailRequestId}\``,
        "yellow",
      );
    case "media": {
      // The signed URL is why this part exists: a screenshot or a recording is
      // the session's proof, and a reader who cannot open it has nothing.
      const target = part.url ?? part.pathname;
      return `${out.paint("media", "dim")}  ${part.mediaType}${part.caption ? ` — ${out.cell(part.caption)}` : ""}${target ? `\n       ${out.cell(target)}` : ""}`;
    }
    case "advisor":
      return formatAdvisorPart(part);
    case "deliverable":
      return formatDeliverablePart(part, event.sessionId, baseUrl);
    case "error":
      return out.paint(`error  ${truncate(part.message)}`, "red");
    case "external_event":
      return `${out.paint("event", "dim")}  ${out.cell(part.title)}${part.conclusion ? ` — ${out.cell(part.conclusion)}` : ""}`;
    case "system":
      return out.paint(`system ${truncate(part.text, 300)}`, "dim");
    // The session's opening row: with the fenced prompt hidden, this is the
    // only place a CLI reader sees what the run was asked to do.
    case "source_context":
      return `${out.paint("source", "dim")} ${out.cell(
        [part.reference, part.title].filter(Boolean).join(" "),
      )}${part.url ? ` — ${out.cell(part.url)}` : ""}${
        part.body ? `\n       ${truncate(part.body, 400)}` : ""
      }`;
    case "verification":
      return `${out.paint("check", "dim")}  ${out.cell(part.name)} ${out.paint(`(${part.status})`, "dim")}`;
    default:
      return null;
  }
}
